import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MdEdit, MdPerson, MdPeople, MdEmail, MdPhone, MdBusiness, MdLocationOn, MdCalendarMonth } from 'react-icons/md';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { getStaffById } from '../../Services/StaffService';
import { APPRoutes } from '../../Utils/Route';
import { formatUtcToLocalDateTime } from '../../Utils/DateTime';
import { toast } from 'react-toastify';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)' },
  Inactive: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)' },
  Pending: { backgroundColor: 'var(--hc-status-pending-alt-bg)', color: 'var(--hc-blue-700)' },
  Suspended: { backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)' },
};

const formatAddress = (address) => {
  if (!address) {
    return '-';
  }

  const pieces = [
    address.street,
    address.street2,
    address.city,
    address.state,
    address.zipcode ?? address.postal_code,
    address.country,
  ].filter(Boolean);

  return pieces.length ? pieces.join(', ') : '-';
};

const formatName = (staff) => {
  if (!staff) return 'Staff Member';
  const fullName = [staff.first_name, staff.last_name].filter(Boolean).join(' ').trim();
  return fullName || staff.email || 'Staff Member';
};

export default function StaffsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(() => resolveUserPermissionCodes({ permissions, decodedToken }), [permissions, decodedToken]);
  const canEditStaff = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_EDIT, decodedToken);
  const canViewList = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_LIST, decodedToken);

  const staffFromLocation = location.state?.staff || null;
  const [staff, setStaff] = React.useState(staffFromLocation);
  const [loading, setLoading] = React.useState(!staffFromLocation);

  const loadStaff = async () => {
    if (!token) return;

    try {
      setLoading(true);
      // No identity to resolve to (e.g. a view-only user with no staff_id claim) -- there is
      // no "first record" fallback anymore; show the empty state instead.
      let targetId = id && id !== 'first' && id !== ':id' ? id : null;
      if (!targetId) {
        setStaff(null);
        return;
      }
      const response = await getStaffById(targetId, token);
      if (response?.is_success) {
        setStaff(response.data);
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setStaff(null);
      } else {
        toast.error(response?.message || 'Failed to load staff');
        setStaff(null);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Failed to load staff');
      setStaff(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!token) return;
    if (!staffFromLocation) {
      loadStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  const statusName = staff?.status_name || 'Unknown';
  const createdOnLocal = React.useMemo(() => formatUtcToLocalDateTime(staff?.created_on), [staff?.created_on]);
  const modifiedOnLocal = React.useMemo(() => formatUtcToLocalDateTime(staff?.modified_on), [staff?.modified_on]);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex flex-column gap-3">
          <div className="skeleton position-relative overflow-hidden rounded-pill" style={{ height: 40, width: 160 }} />
          <div className="p-3 p-lg-4 mb-4 hc-surface overflow-hidden">
            <div className="row g-4 g-xl-5 align-items-stretch">
              <div className="col-xl-9">
                <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-4 h-100">
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 124, height: 124, borderRadius: 28 }} />
                  <div className="flex-grow-1 w-100">
                    <div className="d-flex gap-2 mb-3">
                      <div className="skeleton position-relative overflow-hidden rounded-3" style={{ height: 28, width: 86 }} />
                      <div className="skeleton position-relative overflow-hidden rounded-3" style={{ height: 28, width: 64 }} />
                    </div>
                    <div className="skeleton mb-2" style={{ height: 38, width: '72%', borderRadius: 10 }} />
                    <div className="skeleton mb-1 rounded-3" style={{ height: 16, width: '86%' }} />
                    <div className="skeleton mb-3 rounded-3" style={{ height: 16, width: '58%' }} />
                    <div className="skeleton mb-2 rounded-3" style={{ height: 20, width: 110 }} />
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="skeleton position-relative overflow-hidden rounded-4 w-100" style={{ height: 86 }} />
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="skeleton position-relative overflow-hidden rounded-4 w-100" style={{ height: 86 }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3">
                <div className="skeleton h-100 rounded-4" style={{ minHeight: 220 }} />
              </div>
            </div>
          </div>
          <div className="row g-4">
            <div className="col-lg-6">
              <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 180, borderRadius: 20 }} />
            </div>
            <div className="col-lg-6">
              <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 180, borderRadius: 20 }} />
            </div>
            <div className="col-12">
              <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 130, borderRadius: 20 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!staff) {
    return (
      <ViewEmptyState
        title="No Staff Members Available"
        description="There are no staff member records available to display."
        icon={MdPeople}
        canViewList={canViewList}
        backRoute={APPRoutes.STAFFS}
        backLabel="Back to Staffs"
      />
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        {canViewList && (
          <BackButton onClick={() => navigate(APPRoutes.STAFFS)} />
        )}

        {canEditStaff ? (
          <button
            type="button"
            className="btn hc-bg-primary text-white d-inline-flex align-items-center gap-2"
            onClick={() => navigate(APPRoutes.STAFFS_EDIT.replace(':id', staff.id))}
          >
            <MdEdit size={18} /> Edit Staff
          </button>
        ) : null}
      </div>

      <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
        <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
        <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

        <div className="row g-4 align-items-center position-relative">
          <div className="col-lg-8">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <span
                className="px-3 py-2 rounded-pill fw-semibold"
                style={{
                  fontSize: '0.82rem',
                  backgroundColor: STATUS_STYLE[statusName]?.backgroundColor || 'var(--hc-status-neutral-bg)',
                  color: STATUS_STYLE[statusName]?.color || 'var(--hc-table-footer-text)',
                }}
              >
                {statusName}
              </span>
            </div>

            <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
              {formatName(staff)}
            </h2>
            <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)', fontSize: '0.95rem' }}>
              <MdEmail size={14} className="me-1" /> {staff.email || '-'}
            </p>
            <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)', fontSize: '0.88rem' }}>
              <MdPhone size={14} className="me-1" /> {staff.phone_number || '-'}
            </p>
          </div>

          <div className="col-lg-4">
            <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="d-flex align-items-center justify-content-center overflow-hidden rounded-3 flex-shrink-0"
                  style={{ width: 56, height: 56, background: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                  <MdPerson size={26} className="text-white" />
                </div>
                <div>
                  <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                    Organization
                  </p>
                  <p className="fw-bold text-white mb-0 fs-5" style={{ lineHeight: 1.1 }}>
                    {staff.organization_name || '-'}
                  </p>
                </div>
              </div>
              <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                Client association and staff details summary.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4 align-items-stretch">
        <div className="col-xl-6 d-flex">
          <div className="p-4 p-lg-5 hc-surface hc-card-primary w-100 h-100 d-flex flex-column justify-content-center" style={{ minHeight: 220 }}>
            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
              <MdBusiness size={22} className="hc-text-primary" /> Client Details
            </h5>
            <p className="mb-2"><span className="fw-semibold">Title:</span> {staff.title || '-'}</p>
            <p className="mb-2"><span className="fw-semibold">Organization:</span> {staff.organization_name || '-'}</p>
            <p className="mb-0"><span className="fw-semibold">Client:</span> {staff.account_name || '-'}</p>
          </div>
        </div>

        <div className="col-xl-6 d-flex">
          <div className="p-4 p-lg-5 hc-surface hc-card-primary w-100 h-100 d-flex flex-column justify-content-center" style={{ minHeight: 220 }}>
            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
              <MdLocationOn size={22} className="hc-text-primary" /> Address
            </h5>
            <p className="mb-0 text-secondary">{formatAddress(staff.address)}</p>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="p-4 p-lg-5 hc-surface hc-card-primary">
            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
              <MdPerson size={22} className="hc-text-primary" /> Audit Info
            </h5>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdPerson size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small text-muted">Created By</div>
                    <div className="fw-medium text-dark">{staff.created_by || '-'}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdCalendarMonth size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small text-muted">Created On</div>
                    <div className="fw-medium text-dark">{createdOnLocal}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdPerson size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small text-muted">Modified By</div>
                    <div className="fw-medium text-dark">{staff.modified_by || '-'}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                    <MdCalendarMonth size={18} className="hc-text-primary" />
                  </span>
                  <div className="text-start">
                    <div className="small text-muted">Modified On</div>
                    <div className="fw-medium text-dark">{modifiedOnLocal}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
